import { useAppSelector, useAppDispatch } from "@/services/hooks";
import React, { useEffect, useMemo, useCallback } from "react";

import IndexNavbar from "@/components/Navbars/AuthNavbar";
import { Link } from "react-router-dom"; // if your project still uses "react-router", keep that import
import { getSitesForSiteselector } from "@/services/siteServices/siteActions";
import {
  Button,
  Card,
  CardBody,
  Container,
  Row,
  Col,
  CardTitle,
} from "reactstrap";

/**
 * The auth slice types `userconnected` as its `{}` initial value; once logged
 * in it holds the decoded session, of which this screen reads only the id.
 */
type ConnectedUser = { user?: { id?: string } };

const SiteSelector = () => {
  const dispatch = useAppDispatch();

  const userconnected = useAppSelector((s) => s.auth.userconnected) as ConnectedUser;
  const sites = useAppSelector((s) => s.sites.siteselector_sites || []);

  const userId = useMemo(
    () => userconnected?.user?.id,
    [userconnected]
  );

  useEffect(() => {
    if (userId) {
      dispatch(getSitesForSiteselector(userId));
    }
  }, [dispatch, userId]);

  // SCRUM-1471: the CMS lives on `editor.<domain>`. The bare domain serves the site's
  // PUBLIC host, so every switch used to land on that site's 404 and the editor was
  // never reached. The server strips the prefix again when it resolves the tenant
  // (`normalizeSiteDomain`, server/utils/siteDomain.js), so `editor.fincompara.pt`
  // resolves to the fincompara site.
  //
  // The `localStorage.currentsite` write that used to sit here is gone with it: the only
  // reader was the line directly below it, reading back what the line above had just
  // written. The site a CMS tab edits comes from the request Host, via
  // GET /api/sites/searchByDomain/domain — never from that key.
  const onClickSite = useCallback((e: React.MouseEvent, site: { domain?: string }) => {
    e.preventDefault();
    const link = `https://editor.${(site?.domain || "").trim()}/admin/dashboard`;
    window.open(link.split(" ").join(""), "_blank");
  }, []);

  return (
    <>
      <IndexNavbar />
      <div className="header bg-info pt-5 pb-7">
        <Container>
          <div className="header-body">
            <Row className="align-items-center mt-7">
              <Col sm="12" md={{ size: 8, offset: 2 }}>
                <h1 className="display-2 text-white font-weight-bold mb-0">
                  My Sites
                </h1>
                <h2 className="display-4 text-white font-weight-light">
                  Select a site to modify, display and open its dashboard
                </h2>

                <Button
                  className="btn btn-default"
                  color="default"
                  to="/admin/dashboard"
                  tag={Link}
                >
                  Current Site Dashboard
                </Button>
              </Col>
            </Row>
          </div>

          <br />

          <Row>
            {sites.map((element) => (
              <Col md="4" key={String(element._id)}>
                <Card className="bg-gradient-transparent">
                  <CardBody>
                    <CardTitle
                      tag="h6"
                      className="h2 font-weight-bold text-primary mb-0"
                    >
                      {element.domain}
                    </CardTitle>

                    <p className="mt-3 mb-0 text-sm">
                      <span className="text-success mr-2">
                        Country : {element.country_code}
                      </span>
                      <br />
                      <span className="text-nowrap">
                        Language : {element.language_code}
                      </span>
                    </p>

                    <button
                      className="btn btn-outline-danger mr-1 float-right"
                      type="button"
                      onClick={(e) => onClickSite(e, element)}
                    >
                      Dashboard
                    </button>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>

        <div className="separator separator-bottom separator-skew zindex-100">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="none"
            version="1.1"
            viewBox="0 0 2560 100"
            x="0"
            y="0"
          >
            <polygon className="fill-default" points="2560 0 2560 100 0 100" />
          </svg>
        </div>
      </div>
    </>
  );
};

export default SiteSelector;
